import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Sun, Moon, Sunset, Sparkles, Heart, Leaf, NotebookPen } from 'lucide-react';
import { Button, Card, Input, MoodSelector, type Mood } from '../../components/ui';
import { dailyCheckInService } from '../../services';
import './MorningCheckin.css';

// Warm, encouraging quotes
const quotes = [
  { text: "Every day is a new canvas.", author: "Unknown", emoji: "🎨" },
  { text: "The secret of getting ahead is getting started.", author: "Mark Twain", emoji: "🌱" },
  { text: "Creativity takes courage.", author: "Henri Matisse", emoji: "💜" },
  { text: "Start where you are. Use what you have. Do what you can.", author: "Arthur Ashe", emoji: "✨" },
  { text: "You don't have to be perfect to be wonderful.", author: "Unknown", emoji: "🌸" },
  { text: "Your art doesn't need to be finished to be valuable.", author: "Unknown", emoji: "💫" },
];

// Gentle prompts for intentions
const intentionPrompts = [
  "Maybe: Practice that tricky part...",
  "Maybe: Mix the chorus section...",
  "Maybe: Just open the project...",
  "Maybe: Sketch one new idea...",
  "Maybe: Review yesterday's work...",
];

// Celebration messages
const celebrationMessages = [
  "You showed up for yourself today.",
  "Every check-in is a small victory.",
  "Being aware is the first step to creating.",
  "Your commitment to your craft is beautiful.",
];

export const MorningCheckin: React.FC = () => {
  const navigate = useNavigate();
  const [selectedMood, setSelectedMood] = useState<Mood | undefined>();
  const [intention, setIntention] = useState('');
  const [didMorningPages, setDidMorningPages] = useState<boolean | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [intentionPlaceholder, setIntentionPlaceholder] = useState('');
  const [quote, setQuote] = useState(quotes[0]);
  const [celebrationMessage, setCelebrationMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [existingCheckIn, setExistingCheckIn] = useState<{ id: string; mood: string; dailyIntention: string | null } | null>(null);

  const userName = 'Creator';

  // Check if user already has a check-in for today
  useEffect(() => {
    const checkExistingCheckIn = async () => {
      try {
        const todayCheckIn = await dailyCheckInService.getTodayCheckIn();
        if (todayCheckIn) {
          setExistingCheckIn(todayCheckIn);
          setSelectedMood(todayCheckIn.mood.toLowerCase() as Mood);
          setIntention(todayCheckIn.dailyIntention || '');
          setIsComplete(true);
        }
      } catch (error) {
        console.error('Failed to check existing check-in:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkExistingCheckIn();
  }, []);

  useEffect(() => {
    setQuote(quotes[Math.floor(Math.random() * quotes.length)]);
    setIntentionPlaceholder(intentionPrompts[Math.floor(Math.random() * intentionPrompts.length)]);
    setCelebrationMessage(celebrationMessages[Math.floor(Math.random() * celebrationMessages.length)]);
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return {
      text: 'Good morning',
      Icon: Sun,
      subtext: 'A new day, a fresh start.',
      emoji: '🌅'
    };
    if (hour < 18) return {
      text: 'Good afternoon',
      Icon: Sunset,
      subtext: 'Still time to create something.',
      emoji: '🌤️'
    };
    return {
      text: 'Good evening',
      Icon: Moon,
      subtext: 'The quiet hours can be magical.',
      emoji: '🌙'
    };
  };

  const { text: greetingText, Icon: GreetingIcon, subtext, emoji: greetingEmoji } = getGreeting();

  const handleSubmit = async () => {
    if (!selectedMood) return;
    setIsSubmitting(true);

    try {
      await dailyCheckInService.createCheckIn({
        mood: selectedMood,
        notes: undefined,
        dailyIntention: intention || undefined,
      });
      setIsComplete(true);
    } catch (error) {
      console.error('Failed to create check-in:', error);
      alert('Failed to save your check-in. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getMoodMessage = (mood: Mood) => {
    const messages: Record<Mood, { title: string; subtitle: string; emoji: string; encouragement: string }> = {
      inspired: {
        title: "What a wonderful feeling!",
        subtitle: 'Let that inspiration guide you today.',
        emoji: '✨',
        encouragement: "Channel this into something you love.",
      },
      energized: {
        title: 'Feeling the energy!',
        subtitle: 'Great days make great progress.',
        emoji: '⚡',
        encouragement: "What would feel amazing to work on?",
      },
      okay: {
        title: 'Okay days count too.',
        subtitle: 'Not every day needs to be extraordinary.',
        emoji: '🌤️',
        encouragement: "Be gentle with yourself.",
      },
      tired: {
        title: 'It\'s okay to feel tired.',
        subtitle: 'Rest is part of the creative process.',
        emoji: '🫖',
        encouragement: "Even 10 minutes is still something.",
      },
      blocked: {
        title: 'Take a deep breath.',
        subtitle: 'Creative blocks happen to everyone.',
        emoji: '🌫️',
        encouragement: "One small thing. That's enough.",
      },
    };
    return messages[mood];
  };

  // Show loading state while checking for existing check-in
  if (isLoading) {
    return (
      <div className="checkin-screen">
        <div className="checkin-loading">
          <div className="checkin-loading__spinner" />
          <p>Loading your check-in...</p>
        </div>
      </div>
    );
  }

  if (isComplete && selectedMood) {
    const message = getMoodMessage(selectedMood);
    const isExisting = !!existingCheckIn;

    return (
      <div className="checkin-screen">
        {/* Celebration background - only for new check-ins */}
        {!isExisting && (
          <div className="checkin-celebration-bg">
            {[...Array(12)].map((_, i) => (
              <div key={i} className={`confetti confetti--${i + 1}`} />
            ))}
          </div>
        )}

        <div className={`checkin-complete ${isVisible ? 'checkin-complete--visible' : ''}`}>
          {/* Success animation */}
          <div className="checkin-complete__success">
            <div className="checkin-complete__circle">
              <div className="checkin-complete__circle-inner" />
            </div>
            <span className="checkin-complete__emoji">{message.emoji}</span>
          </div>

          <div className="checkin-complete__message">
            <h1 className="checkin-complete__title">
              {isExisting ? "You've already checked in today!" : message.title}
            </h1>
            <p className="checkin-complete__subtitle">
              {isExisting ? "Here's how you're feeling today." : message.subtitle}
            </p>
          </div>

          <div className="checkin-complete__affirmation">
            <Sparkles size={16} />
            <span>{isExisting ? "Keep going, you're doing great!" : celebrationMessage}</span>
          </div>

          {intention && (
            <Card variant="elevated" padding="md" className="checkin-complete__intention">
              <div className="checkin-complete__intention-header">
                <Leaf size={16} />
                <span className="checkin-complete__intention-label">Your focus today</span>
              </div>
              <p className="checkin-complete__intention-text">"{intention}"</p>
            </Card>
          )}

          <p className="checkin-complete__encouragement">
            {message.encouragement}
          </p>

          <div className="checkin-complete__actions">
            <Button fullWidth size="lg" onClick={() => navigate('/dashboard')}>
              {isExisting ? 'Go to Dashboard' : "Let's Go"} <span className="btn-arrow">→</span>
            </Button>
            <div className="checkin-complete__quick-actions">
              <button className="checkin-quick-action" onClick={() => navigate('/morning-pages')}>
                <span className="checkin-quick-action__icon">📓</span>
                <span>Morning Pages</span>
              </button>
              <button className="checkin-quick-action" onClick={() => navigate('/timer')}>
                <span className="checkin-quick-action__icon">⏱️</span>
                <span>Quick Session</span>
              </button>
              <button className="checkin-quick-action" onClick={() => navigate('/ideas')}>
                <span className="checkin-quick-action__icon">💡</span>
                <span>Capture Idea</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkin-screen">
      {/* Ambient background */}
      <div className="checkin-ambient">
        <div className="checkin-ambient__shape checkin-ambient__shape--1" />
        <div className="checkin-ambient__shape checkin-ambient__shape--2" />
        <div className="checkin-ambient__shape checkin-ambient__shape--3" />
      </div>

      <div className={`checkin-content ${isVisible ? 'checkin-content--visible' : ''}`}>
        {/* Header */}
        <header className="checkin-header">
          <Link to="/dashboard" className="checkin-back">
            <ArrowLeft size={24} />
          </Link>
          <span className="checkin-header__title">Daily Check-in</span>
          <div className="checkin-header__spacer" />
        </header>

        {/* Greeting - More warm and personal */}
        <div className="checkin-greeting">
          <div className="checkin-greeting__icon-wrap">
            <span className="checkin-greeting__emoji">{greetingEmoji}</span>
            <GreetingIcon size={28} className="checkin-greeting__icon" />
          </div>
          <h1 className="checkin-greeting__text">
            {greetingText}, {userName}
          </h1>
          <p className="checkin-greeting__subtext">{subtext}</p>
        </div>

        {/* Quote Card - More alive */}
        <Card variant="default" padding="md" className="checkin-quote">
          <span className="checkin-quote__emoji">{quote.emoji}</span>
          <p className="checkin-quote__text">"{quote.text}"</p>
          <span className="checkin-quote__author">— {quote.author}</span>
        </Card>

        {/* Mood Selection - More engaging */}
        <section className="checkin-section">
          <div className="checkin-section__header">
            <Heart size={18} className="checkin-section__icon" />
            <h2 className="checkin-section__title">How are you feeling?</h2>
          </div>
          <p className="checkin-section__subtitle">Be honest. There's no wrong answer.</p>
          <MoodSelector value={selectedMood} onChange={setSelectedMood} size="lg" />
          {selectedMood && (
            <div className="checkin-mood-response animate-fade-in">
              <span className="checkin-mood-response__emoji">
                {getMoodMessage(selectedMood).emoji}
              </span>
              <span className="checkin-mood-response__text">
                {getMoodMessage(selectedMood).subtitle}
              </span>
            </div>
          )}
        </section>

        {/* Daily Intention - More inviting */}
        <section className="checkin-section">
          <div className="checkin-section__header">
            <Leaf size={18} className="checkin-section__icon" />
            <h2 className="checkin-section__title">Set a gentle intention</h2>
          </div>
          <p className="checkin-section__subtitle">Optional. What would feel good to focus on?</p>
          <Input
            placeholder={intentionPlaceholder}
            value={intention}
            onChange={(e) => setIntention(e.target.value)}
            fullWidth
          />
          <p className="checkin-section__hint">
            Tip: Keep it small and achievable.
          </p>
        </section>

        {/* Morning Pages - More warm */}
        <section className="checkin-section">
          <div className="checkin-section__header">
            <NotebookPen size={18} className="checkin-section__icon" />
            <p className="checkin-section__label">Did you write morning pages today?</p>
          </div>
          <div className="checkin-morning-pages">
            <button
              type="button"
              className={`checkin-morning-pages__option ${didMorningPages === true ? 'checkin-morning-pages__option--selected' : ''}`}
              onClick={() => setDidMorningPages(true)}
            >
              <span className="checkin-morning-pages__emoji">✨</span>
              <span>Yes!</span>
            </button>
            <button
              type="button"
              className={`checkin-morning-pages__option ${didMorningPages === false ? 'checkin-morning-pages__option--selected' : ''}`}
              onClick={() => setDidMorningPages(false)}
            >
              <span className="checkin-morning-pages__emoji">🌱</span>
              <span>Not yet</span>
            </button>
          </div>
          {didMorningPages === true && (
            <Link to="/morning-pages" className="checkin-morning-pages__upload-cta">
              <Sparkles size={16} />
              <span>Upload your pages to get AI insights!</span>
            </Link>
          )}
          <button type="button" className="checkin-morning-pages__link">
            What are morning pages?
          </button>
        </section>

        {/* Actions - More encouraging */}
        <div className="checkin-actions">
          <Button
            fullWidth
            size="lg"
            onClick={handleSubmit}
            disabled={!selectedMood}
            loading={isSubmitting}
            className="checkin-submit"
          >
            {isSubmitting ? 'Saving...' : 'Begin My Day'}
          </Button>
          <button
            type="button"
            className="checkin-skip"
            onClick={() => navigate('/dashboard')}
          >
            I'll check in later
          </button>
        </div>

        {/* Footer encouragement */}
        <div className="checkin-footer">
          <p>Taking a moment to check in is an act of self-care.</p>
        </div>
      </div>
    </div>
  );
};

export default MorningCheckin;
